import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';
import type { OfferType } from '@makemydeal/dr-platform-types';
import { FINANCE } from '@makemydeal/dr-platform-types';
import { FLEX_BUY_DEAL, MONEY_FACTOR, PERCENTAGE_SIGN } from '../../../constants';

const EMPTY_DISPLAY = '';

export const getCashDealLabel = (totalAmountDue: number, cashDown: number, isCashDownPaymentFieldEnabled = false) => {
    // US1988502: when enableCashDownPaymentField is ON, Cash Down is part of the breakdown
    // math stack and `totalAmountDue` is already net of `holdDeposit`, so the secondary
    // "Down / Balance" line is unnecessary — only show "Cash, $X". When OFF, preserve the
    // legacy MV-side subtraction display.
    if (isCashDownPaymentFieldEnabled || cashDown == 0) {
        return [`Cash, ${formatDollarsAndCents(totalAmountDue)}`];
    }
    return [
        `Cash, ${formatDollarsAndCents(totalAmountDue)}`,
        `Down: ${formatDollarsAndCents(cashDown)}  Balance: ${formatDollarsAndCents(totalAmountDue - cashDown)}`
    ];
};

export const getLeaseDealLabel = ({
    amountPaidByCustomer,
    months = 0,
    monthlyPayment,
    rateTypeVal,
    rateTypeLabel,
    isOnePayLease,
    dueAtSigning
}: {
    amountPaidByCustomer: number;
    months?: number;
    monthlyPayment: number;
    rateTypeVal: number;
    rateTypeLabel: string;
    isOnePayLease?: boolean;
    dueAtSigning?: number;
}) => {
    if (isOnePayLease) {
        return [`Lease ${months}, ${formatDollarsAndCents(dueAtSigning)}/OP - ${rateTypeVal} ${rateTypeLabel}`];
    }

    const rateTypePercent = rateTypeLabel.trim() === MONEY_FACTOR ? EMPTY_DISPLAY : PERCENTAGE_SIGN;
    return [
        `Lease, ${months}mo ${formatDollarsAndCents(monthlyPayment)}/mo`,
        `${rateTypeVal}${rateTypePercent} ${rateTypeLabel}  Amt. Paid by Customer: ${formatDollarsAndCents(amountPaidByCustomer)}`
    ];
};

export const getFinanceDealLabel = ({
    downPayment,
    months = 0,
    monthlyPayment,
    rateTypeVal,
    rateTypeLabel,
    flexBuy
}: {
    downPayment: number;
    months?: number;
    monthlyPayment: number;
    rateTypeVal: number;
    rateTypeLabel: string;
    flexBuy?: {
        isFlexBuy: boolean;
        firstTerm: number | undefined;
        firstPayment: number | undefined;
        secondTerm: number | undefined;
        secondPayment: number | undefined;
    };
}) => {
    if (flexBuy?.isFlexBuy) {
        return [
            `Flex Buy, ${flexBuy.firstTerm ?? EMPTY_DISPLAY}/${flexBuy.secondTerm ?? EMPTY_DISPLAY}mo - ${formatDollarsAndCents(
                flexBuy.firstPayment
            )}/${formatDollarsAndCents(flexBuy.secondPayment)}`,
            `${rateTypeVal}% ${rateTypeLabel}  Cash Down: ${formatDollarsAndCents(downPayment)}`
        ];
    }
    return [
        `Finance, ${months}mo ${formatDollarsAndCents(monthlyPayment)}/mo`,
        `${rateTypeVal}% ${rateTypeLabel}  Cash Down: ${formatDollarsAndCents(downPayment)}`
    ];
};

export const mapOfferType = (offerType: string): OfferType => {
    const offerTypeMapping: { [key: string]: OfferType } = {
        [FLEX_BUY_DEAL]: FINANCE
    };

    return offerTypeMapping[offerType] || (offerType.toLowerCase() as OfferType);
};

export const getBalloonDealLabel = ({
    downPayment,
    months = 0,
    monthlyPayment,
    rateTypeVal,
    rateTypeLabel,
    balloonAmount
}: {
    downPayment: number;
    months?: number;
    monthlyPayment: number;
    rateTypeVal: number;
    rateTypeLabel: string;
    balloonAmount: number;
}) => {
    return [
        `Balloon, ${months}mo ${formatDollarsAndCents(monthlyPayment)}/mo`,
        `${rateTypeVal}% ${rateTypeLabel}  Cash Down: ${formatDollarsAndCents(downPayment)} Balloon: ${formatDollarsAndCents(
            balloonAmount
        )}`
    ];
};
